import type { Directive } from "vue";

// 扩展 HTMLElement 接口，添加 __resizeHandler__ 属性
declare global {
  interface HTMLElement {
    __resizeHandler__?: () => void;
    __textObserver__?: MutationObserver;
    __measureEl__?: HTMLDivElement;
    __animationHandler__?: (event: AnimationEvent) => void;
  }
}

const resizeText: Directive = {
  mounted(el: HTMLElement, binding: any) {
    // 获取传入的参数，默认为 {}
    const options = binding.value || {};

    // 设置默认参数
    const config = {
      initialFontSize: options.initialFontSize ?? 60, // 初始字体大小
      minFontSize: options.minFontSize ?? 12, // 最小字体大小
      maxFontSize: options.maxFontSize ?? 100, // 最大字体大小
      maxLines: options.maxLines ?? 1, // 最大行数，默认为1行
      adjustOnResize: options.adjustOnResize ?? true, // 是否监听窗口变化
    };

    // 确保容器样式正确（根据项目规范）
    el.style.overflow = "hidden";
    if (config.maxLines === 1) {
      el.style.whiteSpace = "nowrap";
    } else {
      el.style.display = "-webkit-box";
      el.style.webkitLineClamp = String(config.maxLines);
      el.style.webkitBoxOrient = "vertical";
      el.style.overflow = "hidden";
    }

    // 创建用于测量的辅助元素
    const measureEl = document.createElement("div");
    measureEl.style.position = "absolute";
    measureEl.style.visibility = "hidden";
    measureEl.style.left = "-9999px";
    measureEl.style.top = "-9999px";
    measureEl.style.width = el.offsetWidth + "px";
    // 复制关键样式
    const computedStyle = getComputedStyle(el);
    measureEl.style.fontFamily = computedStyle.fontFamily;
    measureEl.style.fontWeight = computedStyle.fontWeight;
    measureEl.style.letterSpacing = computedStyle.letterSpacing;
    measureEl.style.wordSpacing = computedStyle.wordSpacing;
    document.body.appendChild(measureEl);

    // 保存测量元素引用以便卸载时清理
    el.__measureEl__ = measureEl;

    // 动态调整字体大小
    const resize = () => {
      // 初始字体大小不能超过最大值
      let fontSize = Math.min(config.initialFontSize, config.maxFontSize);
      el.style.fontSize = fontSize + "px";
      measureEl.style.fontSize = fontSize + "px";
      measureEl.style.width = el.offsetWidth + "px";

      // 计算目标高度（基于行数和行高）
      const currentComputedStyle = getComputedStyle(el);
      const lineHeight =
        parseFloat(currentComputedStyle.lineHeight) || fontSize * 1.5;
      const targetHeight = lineHeight * config.maxLines;

      // 根据行数模式采用不同的检测策略
      if (config.maxLines === 1) {
        // 单行模式：检测宽度溢出
        while (el.scrollWidth > el.offsetWidth) {
          if (fontSize <= config.minFontSize) {
            break;
          } else if (fontSize > config.maxFontSize) {
            el.style.fontSize = config.maxFontSize + "px";
            measureEl.style.fontSize = config.maxFontSize + "px";
            break;
          }
          fontSize--;
          el.style.fontSize = fontSize + "px";
          measureEl.style.fontSize = fontSize + "px";
        }
      } else {
        // 多行模式：通过测量元素检测实际行数
        measureEl.textContent = el.textContent;
        measureEl.style.whiteSpace = "normal";
        measureEl.style.wordWrap = "break-word";
        measureEl.style.overflow = "visible";

        while (measureEl.scrollHeight > targetHeight) {
          if (fontSize <= config.minFontSize) {
            break;
          } else if (fontSize > config.maxFontSize) {
            el.style.fontSize = config.maxFontSize + "px";
            measureEl.style.fontSize = config.maxFontSize + "px";
            break;
          }
          fontSize--;
          el.style.fontSize = fontSize + "px";
          measureEl.style.fontSize = fontSize + "px";
        }
      }
    };

    // 初始调整
    resize();

    // 🎯 新增：监听动画结束事件，确保动画完成后重新计算字体大小
    const handleAnimationEnd = () => {
      console.log("🔄 动画结束，重新调整字体大小");
      requestAnimationFrame(resize);
    };

    el.addEventListener("animationend", handleAnimationEnd);
    el.addEventListener("webkitAnimationEnd", handleAnimationEnd);

    // 保存动画处理函数引用以便卸载时清理
    el.__animationHandler__ = handleAnimationEnd;

    // 使用 MutationObserver 监听文本内容变化
    const textObserver = new MutationObserver(() => {
      // 文本内容变化时重新调整字体
      requestAnimationFrame(resize);
    });

    // 监听子节点变化和字符数据变化
    textObserver.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // 保存 observer 引用以便卸载时清理
    el.__textObserver__ = textObserver;

    // 监听窗口变化（可选）
    let handler: (() => void) | null = null;
    if (config.adjustOnResize) {
      handler = () => requestAnimationFrame(resize);
      window.addEventListener("resize", handler);
      el.__resizeHandler__ = handler;
    }

    // 处理元素被隐藏后重新显示的情况
    if (window.getComputedStyle(el).display === "none") {
      const observer = new MutationObserver(() => {
        if (window.getComputedStyle(el).display !== "none") {
          resize();
          if (handler) {
            observer.disconnect();
          }
        }
      });
      observer.observe(el.parentElement!, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }
  },

  unmounted(el: any) {
    // 🎯 移除动画结束事件监听
    if (el.__animationHandler__) {
      el.removeEventListener("animationend", el.__animationHandler__);
      el.removeEventListener("webkitAnimationEnd", el.__animationHandler__);
      el.__animationHandler__ = undefined;
    }

    // 移除文本内容监听器
    if (el.__textObserver__) {
      el.__textObserver__.disconnect();
      el.__textObserver__ = undefined;
    }

    // 移除事件监听
    if (el.__resizeHandler__) {
      window.removeEventListener("resize", el.__resizeHandler__);
      el.__resizeHandler__ = undefined;
    }

    // 移除测量元素
    if (el.__measureEl__) {
      el.__measureEl__.remove();
      el.__measureEl__ = undefined;
    }
  },
};

export default resizeText;
